import * as React from 'react';
import { useCallback } from 'react';

import { ErrorRemovingLastFavoriteStoreModal } from 'components/modal-error-removing-last-favorite-store';
import { InternalStoreDiagnosticModal } from 'components/store-diagnostic-modal';
import { useFavoriteStore } from 'hooks/favorite-stores';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useStoreActions } from 'pages/store-locator/hooks/use-store-actions';
import { useStoreContext } from 'state/store';

import { useDiagnostics } from './hooks/use-diagnostics';
import { useErrorMessage } from './hooks/use-error-message';
import { useOpenClosedText } from './hooks/use-open-closed-text';
import { useServiceModeText } from './hooks/use-service-mode-text';
import { Wrapper } from './store-card.styled';
import { StoreCardView } from './store-card.view';
import { StoreCardProps } from './types';

/**
 * StoreCard container component. This component is intended to be used on the
 * store locator. Data orchestration should happen at this level while keeping
 * the view layer more presentational.
 */
export const StoreCard: React.FC<StoreCardProps> = React.memo(
  ({ restaurant, onCardPress, isSelected, isInMapView, isInSupportForm, selectStoreFormField }) => {
    const distanceText = useDistanceText({ restaurant });
    const errorMessage = useErrorMessage({ restaurant });
    const { store } = useStoreContext();
    const { restaurantCanBeSelected } = useServiceModeStatus(restaurant);
    const storeIsSelectedOnServiceMode = store._id === restaurant._id;
    const serviceModesText = useServiceModeText({ restaurant });
    const {
      data: diagnosticsData,
      showModal: showDiagnosticsModal,
      onClick: onDiagnosticsClick,
      onDismiss: showDiagnosticsModalDismiss,
    } = useDiagnostics({ restaurant });

    const { onInfoPress, onOrderPress } = useStoreActions({ restaurant });

    const {
      errorFavoriteStoreDialog,
      favStoreLoading,
      isFavorite,
      toggleFavorite: onFavoriteClick,
      hasErrorRemovingLastFavStore,
      onDismissErrorRemovingLastFavStoreModal,
    } = useFavoriteStore(restaurant._id ?? '', restaurant.number ?? '');

    const { isOpen, isWithinOneHourOfClose, text } = useOpenClosedText(restaurant);

    useLogUserErrorMessage({
      message: errorMessage,
      logWhenTrue: !!errorMessage && isOpen,
      severity: LogUserErrorSeverity.Informational,
      context: {
        storeId: restaurant?.storeId,
      },
    });

    const isActive = (isOpen || isWithinOneHourOfClose) && restaurantCanBeSelected;

    const handleOnCardPress = useCallback(() => isActive && onCardPress?.({ restaurant }), [
      isActive,
      onCardPress,
      restaurant,
    ]);

    const handleonOrderPress = () => {
      if (selectStoreFormField && restaurant._id) {
        selectStoreFormField(restaurant._id);
        return;
      }
      onOrderPress();
    };

    return (
      <Wrapper testID="store-card" isSelected={storeIsSelectedOnServiceMode}>
        <StoreCardView
          onCardPress={handleOnCardPress}
          onDiagnosticsClick={onDiagnosticsClick}
          onFavoriteClick={onFavoriteClick}
          onInfoPress={onInfoPress}
          onOrderPress={handleonOrderPress}
          isFavorite={isFavorite}
          isUpdatingFavorites={favStoreLoading}
          isSelected={isSelected}
          storeDetails={{
            serviceModesText,
            distanceText,
            restaurantStatus: {
              isOpen,
              isWithinOneHourOfClose,
              text,
            },
            address: restaurant.physicalAddress?.address1,
            isHalal: Boolean(restaurant.isHalal),
            restaurant,
            isActive,
          }}
          restaurantCanBeSelected={restaurantCanBeSelected}
          errorMessage={errorMessage}
          isInMapView={isInMapView}
          isInSupportForm={isInSupportForm}
        />
        {showDiagnosticsModal && (
          <InternalStoreDiagnosticModal
            onModalDismiss={showDiagnosticsModalDismiss}
            storeDiagnosticData={diagnosticsData}
          />
        )}
        {hasErrorRemovingLastFavStore && (
          <ErrorRemovingLastFavoriteStoreModal
            onDismiss={onDismissErrorRemovingLastFavStoreModal}
          />
        )}
        {errorFavoriteStoreDialog}
      </Wrapper>
    );
  }
);
